import type { HttpErrorResponse } from '@angular/common/http';

import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  PickActions,
  pipeState,
} from '@core/ngrx-legacy';
import {
  AutomationAction,
  ExecutedAction,
  ExecutedAutomation,
} from '@core/workflow-automations-types';

export const getOfferScope = (offerId: number): string => `offer:${offerId}`;
export const getCandidateScope = (candidateId: number): string => `candidate:${candidateId}`;

const idSelector = (action: { id: number }) => action.id;

const { actions, selectors, reducer, namespace } =
  createEntityCollectionStore<ExecutedAutomation>()('executedAutomations', {
    idSelector,
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        fetchOfferCollection: (state, payload: { offerId: number }) =>
          defaultActions.fetchCollection(state, {
            scope: getOfferScope(payload.offerId),
            ...payload,
          }),
        fetchOfferCollectionSuccess: (
          state,
          data: { offerId: number; executedAutomations: ExecutedAutomation[] },
        ) =>
          pipeState(
            state,
            next => adapter.resetScope(next, getOfferScope(data.offerId)),
            next =>
              adapter.setMany(next, data.executedAutomations, {
                scope: getOfferScope(data.offerId),
              }),
          ),
        fetchOfferCollectionFailed: defaultActions.fetchCollectionFailed,

        fetchCandidateCollection: (state, payload: { candidateId: number }) =>
          defaultActions.fetchCollection(state, {
            scope: getCandidateScope(payload.candidateId),
            ...payload,
          }),
        fetchCandidateCollectionSuccess: (
          state,
          data: { candidateId: number; executedAutomations: ExecutedAutomation[] },
        ) =>
          pipeState(
            state,
            next => adapter.resetScope(next, getCandidateScope(data.candidateId)),
            next =>
              adapter.setMany(next, data.executedAutomations, {
                scope: getCandidateScope(data.candidateId),
              }),
          ),
        fetchCandidateCollectionFailed: defaultActions.fetchCollectionFailed,

        cancelExecutedAction: ngrxEvent<{
          executedAutomationId: number;
          executedActionId: number;
        }>(),
        cancelExecutedActionSuccess: ngrxEvent<{
          executedAutomationId: number;
          executedActionId: number;
        }>(),
        cancelExecutedActionFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        cancelExecutedAutomation: ngrxEvent<{ executedAutomationId: number }>(),
        cancelExecutedAutomationSuccess: ngrxEvent<{
          executedAutomationId: number;
        }>(),
        cancelExecutedAutomationFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        updateExecutedAction: ngrxEvent<{
          executedAutomationId: number;
          executedActionId: number;
          executedAction: {
            snapshot: AutomationAction;
          };
        }>(),
        updateExecutedActionSuccess: ngrxEvent<{
          executedAutomationId: number;
          executedAction: ExecutedAction;
        }>(),
        updateExecutedActionFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
      };
    },
  });

const ExecutedAutomationsActions: PickActions<
  typeof actions,
  | 'fetchOfferCollection'
  | 'fetchCandidateCollection'
  | 'cancelExecutedAction'
  | 'cancelExecutedAutomation'
  | 'updateExecutedAction'
> = actions;

export {
  ExecutedAutomationsActions,
  selectors as ExecutedAutomationsSelectors,
  reducer as ExecutedAutomationsReducer,
  namespace as ExecutedAutomationsNamespace,
};
